<script setup lang="ts">
import {TextInput, useClose} from "@hantera/design-system";
import {ref} from "vue";
import {useLocalization} from '@hantera/portal-app'
import useBackend from "@/composition/useBackend";

interface Props {
  currentEmail: string
  onEmailChanged?: () => void
}

const props = defineProps<Props>()

const { user } = useBackend()
const t = useLocalization('user')
const close = useClose()

const newEmail = ref(props.currentEmail)
const loading = ref(false)
const emailInput = ref()

async function submit() {
  // Validate email
  if (!newEmail.value?.trim()) {
    emailInput.value.setError(t('validation.emailRequired'))
    return
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.\w+$/
  if (!emailRegex.test(newEmail.value)) {
    emailInput.value.setError(t('validation.emailInvalid'))
    return
  }

  if (newEmail.value.trim() === props.currentEmail) {
    emailInput.value.setError(t('validation.emailSame'))
    return
  }

  loading.value = true

  try {
    await user.changeEmail(newEmail.value.trim())
    props.onEmailChanged?.()
    close()
  } catch (err: any) {
    emailInput.value.setError(err.message || t('errors.unexpectedError'))
  } finally {
    loading.value = false
  }
}

function cancel() {
  close()
}
</script>

<template>
  <form @submit.prevent="submit" class="change-email-dialog">
    <div class="field">
      <label for="new-email">{{ t('profile.email') }}</label>
      <TextInput 
        id="new-email" 
        type="email" 
        v-model="newEmail" 
        :disabled="loading"
        @keydown.enter="submit"
        ref="emailInput"
      />
    </div>

    <p class="help-text">{{ t('messages.checkEmail') }}</p>

    <div class="actions">
      <button type="button" @click="cancel" class="btn-silent" :disabled="loading">
        {{ t('actions.cancel') }}
      </button>
      <button type="submit" class="btn-primary" :disabled="loading">
        {{ loading ? t('actions.resending') : t('actions.sendVerification') }}
      </button>
    </div>
  </form>
</template>

<style scoped>
.change-email-dialog {
  padding: 1rem;
  min-width: 400px;
}

.field {
  margin-bottom: 1rem;
}

.help-text {
  font-size: 0.875rem;
  color: #666;
  margin: 0.5rem 0 1rem 0;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: #ffebee;
  border-radius: 3px;
}

.actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

@media (max-width: 768px) {
  .change-email-dialog {
    min-width: auto;
  }

  .actions {
    flex-direction: column;
  }

  .actions button {
    width: 100%;
  }
}
</style>
